import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import {
  Phone, Globe, MapPin, Star, Clock, Users, Award,
  CheckCircle, Heart, Shield, Calendar, ChevronLeft, Loader2
} from 'lucide-react';
import { facilityApi } from '../utils/api';
import { mockStates } from '../data/mock';
import { toSafeJsonLdString } from '../utils/jsonLd';

const FacilityDetailPage = () => {
  const { id } = useParams();

  const [loading, setLoading] = useState(true);
  const [facility, setFacility] = useState(null);
  const [relatedFacilities, setRelatedFacilities] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const fetchFacility = async () => {
      setLoading(true);
      setNotFound(false);

      try {
        const data = await facilityApi.getById(id);
        setFacility(data);

        // Fetch a few more facilities from the same state for the sidebar
        if (data?.state) {
          try {
            const stateResponse = await facilityApi.getByState(data.state, { limit: 4 });
            setRelatedFacilities(
              (stateResponse.facilities || []).filter(f => f.id !== data.id).slice(0, 3)
            );
          } catch (relatedErr) {
            console.error('Error fetching related facilities:', relatedErr);
            setRelatedFacilities([]);
          }
        }
      } catch (err) {
        console.error('Error fetching facility:', err);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };

    fetchFacility();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="text-center py-12">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-blue-600" />
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Loading Treatment Center</h2>
            <p className="text-gray-600">Fetching facility details...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (notFound || !facility) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="text-center py-12">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Facility Not Found</h1>
            <p className="text-gray-600 mb-6">
              We couldn't find the treatment facility you're looking for.
            </p>
            <Button asChild>
              <Link to="/search">Browse All Facilities</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Backend returns state_full_name (snake_case); fall back to the static state list, then the code itself
  const stateFullName =
    facility.state_full_name ||
    facility.stateFullName ||
    mockStates.find(s => s.code === facility.state)?.name ||
    facility.state;

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "MedicalBusiness",
    "name": facility.name,
    "description": facility.description,
    "address": {
      "@type": "PostalAddress",
      "streetAddress": facility.address?.split(',')[0],
      "addressLocality": facility.city,
      "addressRegion": facility.state,
      "addressCountry": "US"
    },
    "telephone": facility.phone,
    "url": facility.website,
    "aggregateRating": facility.rating ? {
      "@type": "AggregateRating",
      "ratingValue": facility.rating,
      "reviewCount": facility.reviews
    } : undefined
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Breadcrumb */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <Link to="/" className="hover:text-blue-600">Home</Link>
            <span>/</span>
            <Link to="/search" className="hover:text-blue-600">Search</Link>
            <span>/</span>
            <Link to={`/states/${facility.state.toLowerCase()}`} className="hover:text-blue-600">
              {stateFullName}
            </Link>
            <span>/</span>
            <span className="text-gray-900">{facility.name}</span>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Header */}
            <Card>
              <CardContent className="p-8">
                <div className="flex items-start justify-between mb-6">
                  <div className="flex-1">
                    <h1 className="text-3xl font-bold text-gray-900 mb-3">
                      {facility.name}
                    </h1>
                    
                    <div className="flex items-center space-x-4 mb-4">
                      <div className="flex items-center text-gray-600">
                        <MapPin className="h-5 w-5 mr-2" />
                        <span>{facility.city}, {stateFullName}</span>
                      </div>
                      
                      {facility.rating && (
                        <div className="flex items-center space-x-1">
                          <Star className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                          <span className="font-medium">{facility.rating}</span>
                          <span className="text-gray-500">({facility.reviews} reviews)</span>
                        </div>
                      )}
                    </div>
                    
                    {facility.established && (
                      <div className="flex items-center text-sm text-gray-600 mb-4">
                        <Calendar className="h-4 w-4 mr-2" />
                        <span>Established {facility.established}</span>
                      </div>
                    )}
                  </div>
                </div>

                <p className="text-gray-600 mb-6 text-lg">
                  {facility.description}
                </p>

                {/* Quick Actions */}
                <div className="flex flex-col sm:flex-row gap-4">
                  {facility.phone && (
                    <Button asChild size="lg" className="bg-gradient-to-r from-blue-600 to-purple-600">
                      <a href={`tel:${facility.phone}`}>
                        <Phone className="h-5 w-5 mr-2" />
                        Call Now: {facility.phone}
                      </a>
                    </Button>
                  )}

                  {facility.website && (
                    <Button asChild variant="outline" size="lg">
                      <a href={facility.website} target="_blank" rel="noopener noreferrer">
                        <Globe className="h-5 w-5 mr-2" />
                        Visit Website
                      </a>
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Treatment Services */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Heart className="h-5 w-5 mr-2 text-red-500" />
                  Treatment Services & Programs
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-semibold mb-3">Treatment Types</h4>
                    <div className="space-y-2">
                      {facility.treatmentTypes?.map((type) => (
                        <div key={type} className="flex items-center">
                          <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                          <span className="text-sm">{type}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold mb-3">Specializations</h4>
                    <div className="flex flex-wrap gap-2">
                      {facility.specializations?.map((spec) => (
                        <Badge key={spec} variant="outline" className="text-xs">
                          {spec}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Features & Amenities */}
            {facility.features && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Award className="h-5 w-5 mr-2 text-yellow-600" />
                    Features & Amenities
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-4">
                    {facility.features.map((feature) => (
                      <div key={feature} className="flex items-center">
                        <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                        <span className="text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Insurance & Payment */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Shield className="h-5 w-5 mr-2 text-blue-600" />
                  Insurance & Payment Options
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-4">
                  This facility accepts various insurance plans and payment methods:
                </p>
                <div className="grid md:grid-cols-3 gap-2">
                  {facility.insurance?.map((ins) => (
                    <div key={ins} className="flex items-center">
                      <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                      <span className="text-sm">{ins}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Accreditation */}
            {facility.accreditation && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Award className="h-5 w-5 mr-2 text-purple-600" />
                    Accreditation & Certifications
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {facility.accreditation.map((acc) => (
                      <Badge key={acc} className="bg-purple-100 text-purple-700">
                        {acc}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 space-y-6">
              {/* Contact Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Get Help Now</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {facility.phone && (
                    <Button asChild className="w-full bg-gradient-to-r from-blue-600 to-purple-600">
                      <a href={`tel:${facility.phone}`}>
                        <Phone className="h-4 w-4 mr-2" />
                        Call: {facility.phone}
                      </a>
                    </Button>
                  )}

                  {facility.website && (
                    <Button asChild variant="outline" className="w-full">
                      <a href={facility.website} target="_blank" rel="noopener noreferrer">
                        <Globe className="h-4 w-4 mr-2" />
                        Visit Website
                      </a>
                    </Button>
                  )}
                  
                  {facility.email && (
                    <div className="text-sm text-gray-600">
                      <strong>Email:</strong> {facility.email}
                    </div>
                  )}
                  
                  {facility.address && (
                    <div className="text-sm text-gray-600">
                      <strong>Address:</strong> {facility.address}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Emergency Help */}
              <Card className="border-red-200 bg-red-50">
                <CardHeader>
                  <CardTitle className="text-lg text-red-800">Emergency Help</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-red-700 mb-4">
                    If you're experiencing a mental health emergency, call:
                  </p>
                  <div className="space-y-2 text-sm">
                    <div>
                      <strong>Crisis Line:</strong>{' '}
                      <a href="tel:988" className="text-red-600 font-medium">988</a>
                    </div>
                    <div>
                      <strong>SAMHSA Helpline:</strong>{' '}
                      <a href="tel:1-800-662-4357" className="text-red-600 font-medium">
                        1-800-662-4357
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Related Facilities */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">More in {stateFullName}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {relatedFacilities.map((relatedFacility) => (
                      <Link
                        key={relatedFacility.id}
                        to={`/facility/${relatedFacility.id}`}
                        className="block p-3 rounded-lg border hover:bg-gray-50 transition-colors"
                      >
                        <h4 className="font-medium text-sm mb-1 line-clamp-1">
                          {relatedFacility.name}
                        </h4>
                        <p className="text-xs text-gray-600">
                          {relatedFacility.city}, {relatedFacility.state}
                        </p>
                      </Link>
                    ))}

                    <Button asChild variant="outline" size="sm" className="w-full">
                      <Link to={`/states/${facility.state.toLowerCase()}`}>
                        View All in {stateFullName}
                      </Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>

      {/* Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: toSafeJsonLdString(structuredData)
        }}
      />
    </div>
  );
};

export default FacilityDetailPage;